import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { EyeOff, Users } from 'lucide-react';
import { Avatar, Card, EmptyState, SectionHeader, SegmentedControl, Skeleton } from '../../../components/ui';
import type { IssueView, StanceView, SubjectRef } from '../../../types/issues';
import { POSITION_ORDER, positionLabel, positionStyle, subjectHref, subjectKey } from '../components/format';
import { InfluenceBar, PositionGlyph } from '../components/PositionMark';
import { RecordStanceDialog, StanceDefaults } from '../components/RecordStanceDialog';
import { SubjectPicker } from '../components/SubjectPicker';
import { useEntityDirectory, useStances, useUnpositioned } from '../useIssues';
import { ColumnSort, buildColumns } from './grouping';
import { StanceCard } from './StanceCard';
import { StanceHistoryDialog } from './StanceHistoryDialog';

const SORTS: { value: ColumnSort; label: string }[] = [
  { value: 'influence', label: 'Influence' },
  { value: 'recent', label: 'Recent' },
  { value: 'name', label: 'Name' },
];

interface Pending {
  subject: SubjectRef;
  from: number | null;
  position: number;
  defaults?: StanceDefaults;
}

/**
 * Where everyone stands, in five columns. A card is dragged between columns or
 * moved from its menu; either way the change goes through a confirm where the
 * analyst says what it rests on, and is appended to the history rather than
 * overwriting what was there.
 */
export const PositionsBoard: React.FC<{ issue: IssueView; onChange: () => void }> = ({ issue, onChange }) => {
  const { stances, loading, error, unavailable, reload } = useStances(issue.id);
  const blindSpots = useUnpositioned(issue.id);
  const lineOf = useEntityDirectory();

  const [sort, setSort] = useState<ColumnSort>('influence');
  const [dragging, setDragging] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [history, setHistory] = useState<SubjectRef | null>(null);

  const columns = useMemo(() => buildColumns(stances, sort), [stances, sort]);
  const placed = useMemo(() => stances.map((stance) => subjectKey(stance.subject)), [stances]);
  const byKey = useMemo(() => {
    const map = new Map<string, StanceView>();
    stances.forEach((stance) => map.set(subjectKey(stance.subject), stance));
    return map;
  }, [stances]);

  const openMove = (stance: StanceView, position: number) =>
    setPending({
      subject: stance.subject,
      from: stance.position,
      position,
      defaults: {
        issueInfluence: stance.issueInfluence,
        confidence: stance.confidence,
        source: stance.source,
      },
    });

  const openPlacement = (subject: SubjectRef, position: number, influence?: number) =>
    setPending({ subject, from: null, position, defaults: { issueInfluence: influence ?? 50 } });

  const onDrop = (position: number) => {
    setDragOver(null);
    const key = dragging;
    setDragging(null);
    if (!key) return;
    const stance = byKey.get(key);
    if (!stance || stance.position === position) return;
    openMove(stance, position);
  };

  if (unavailable) {
    return (
      <Card>
        <EmptyState icon={Users} title="Issues are not available on this server yet" />
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[12px] text-neutral-400">
          {stances.length} position{stances.length === 1 ? '' : 's'} ·{' '}
          <span className="hidden sm:inline">drag a card or use its menu to move someone</span>
          <span className="sm:hidden">use a card’s menu to move someone</span>
        </p>
        <SegmentedControl label="Sort within a column" items={SORTS} value={sort} onChange={setSort} />
      </div>

      {error && (
        <p role="alert" className="rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
          {error}
        </p>
      )}

      {loading && !stances.length ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {POSITION_ORDER.map((position) => (
            <Skeleton key={position} className="h-40 w-full" />
          ))}
        </div>
      ) : (
        // relative: the columns' absolutely placed labels (sr-only) belong to this scroller; without it they
        // widen the whole page on a phone, which then scrolls sideways
        <div className="relative -mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 [scrollbar-width:none] lg:mx-0 lg:grid lg:grid-cols-5 lg:overflow-visible lg:px-0 lg:pb-0">
          {columns.map((column) => {
            const style = positionStyle(column.position);
            const active = dragOver === column.position;
            return (
              <section
                key={column.position}
                aria-label={positionLabel(column.position)}
                onDragOver={(event) => {
                  event.preventDefault();
                  event.dataTransfer.dropEffect = 'move';
                  if (dragOver !== column.position) setDragOver(column.position);
                }}
                onDragLeave={() => setDragOver((current) => (current === column.position ? null : current))}
                onDrop={(event) => {
                  event.preventDefault();
                  onDrop(column.position);
                }}
                className={`w-[78vw] max-w-[320px] flex-shrink-0 snap-start rounded-2xl p-2.5 transition-colors lg:w-auto lg:max-w-none ${
                  active ? 'bg-neutral-100 ring-2 ring-inset ring-primary-600/25' : 'bg-neutral-50'
                }`}
              >
                <header className="mb-2.5 flex items-center gap-2 px-0.5">
                  <PositionGlyph position={column.position} />
                  <h3 className="min-w-0 flex-1 truncate text-[12.5px] text-ink">{positionLabel(column.position)}</h3>
                  <span className="text-[12px] tabular-nums text-neutral-400" title="Actors in this column">
                    {column.stances.length}
                  </span>
                </header>
                <div className={`mb-2.5 h-0.5 rounded-full ${column.stances.length ? style.solid : 'bg-neutral-200'}`} aria-hidden="true" />

                <div className="space-y-2">
                  {column.stances.map((stance) => (
                    <StanceCard
                      key={stance.id || subjectKey(stance.subject)}
                      stance={stance}
                      line={lineOf(stance.subject.id)}
                      dragging={dragging === subjectKey(stance.subject)}
                      onDragStart={() => setDragging(subjectKey(stance.subject))}
                      onDragEnd={() => {
                        setDragging(null);
                        setDragOver(null);
                      }}
                      onMove={(position) => openMove(stance, position)}
                      onHistory={() => setHistory(stance.subject)}
                    />
                  ))}
                  {!column.stances.length && (
                    <p className="rounded-xl border border-dashed border-neutral-200 px-2.5 py-4 text-center text-[12px] text-neutral-400">
                      Nobody here
                    </p>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {!loading && !stances.length && (
        <Card>
          <EmptyState
            icon={Users}
            title="No positions recorded yet — place the first actor below"
          />
        </Card>
      )}

      {/* ---- blind spots ---- */}
      <section>
        <SectionHeader
          title="No position recorded"
          aside={
            blindSpots.nodes.length ? (
              <span className="tabular-nums">{blindSpots.nodes.length} in scope</span>
            ) : undefined
          }
        />
        <Card>
          <div className="border-b border-neutral-200/70 p-3">
            <SubjectPicker
              onPick={(subject) => openPlacement(subject, 0)}
              exclude={placed}
              placeholder="Add anyone to this issue"
            />
          </div>

          {blindSpots.loading && (
            <div className="space-y-2 p-3">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          )}

          {!blindSpots.loading && !blindSpots.nodes.length && (
            <EmptyState
              icon={EyeOff}
              title={
                blindSpots.available
                  ? 'Everyone in scope has a position'
                  : 'The coalition map is unavailable, so blind spots cannot be listed'
              }
              className="py-8"
            />
          )}

          {!blindSpots.loading && blindSpots.nodes.length > 0 && (
            <ul className="divide-y divide-neutral-200/70">
              {blindSpots.nodes.map((node) => (
                <li key={subjectKey(node.subject)} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2.5">
                  <Avatar name={node.subject.label} kind={node.subject.type} size="sm" />
                  <div className="min-w-0 flex-1">
                    <Link
                      to={subjectHref(node.subject)}
                      className="block truncate text-[13px] text-ink transition-colors hover:text-primary-700"
                    >
                      {node.subject.label}
                    </Link>
                    <p className="truncate text-[11.5px] text-neutral-400">
                      {lineOf(node.subject.id) || (node.subject.type === 'organization' ? 'Organization' : 'Person')}
                    </p>
                  </div>
                  <InfluenceBar
                    value={node.directoryInfluence ?? node.issueInfluence ?? 0}
                    muted
                    label="Directory influence"
                    className="w-24 flex-shrink-0"
                  />
                  <div className="flex flex-shrink-0 items-center gap-1" role="group" aria-label={`Place ${node.subject.label}`}>
                    {POSITION_ORDER.map((position) => {
                      const style = positionStyle(position);
                      const Icon = style.icon;
                      return (
                        <button
                          key={position}
                          type="button"
                          title={`Place as ${positionLabel(position)}`}
                          onClick={() =>
                            openPlacement(node.subject, position, node.directoryInfluence ?? node.issueInfluence ?? 50)
                          }
                          className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-neutral-50 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
                        >
                          <Icon className={`h-3.5 w-3.5 ${style.text}`} strokeWidth={2} />
                          <span className="sr-only">
                            Place {node.subject.label} as {positionLabel(position)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </section>

      {pending && (
        <RecordStanceDialog
          issueId={issue.id}
          subject={pending.subject}
          from={pending.from}
          position={pending.position}
          defaults={pending.defaults}
          onClose={() => setPending(null)}
          onRecorded={async () => {
            setPending(null);
            await reload();
            blindSpots.reload();
            onChange();
          }}
        />
      )}

      {history && (
        <StanceHistoryDialog issueId={issue.id} subject={history} onClose={() => setHistory(null)} />
      )}
    </div>
  );
};

export default PositionsBoard;
